import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { getSEOConfig } from "@/utils/seo-injection";

interface SEOHelmetProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  keywords?: string;
}

const SEOHelmet = ({ 
  title: titleOverride, 
  description: descriptionOverride, 
  canonical: canonicalOverride,
  ogImage: ogImageOverride,
  ogType = "website",
  keywords: keywordsOverride
}: SEOHelmetProps) => {
  const location = useLocation();
  const autoConfig = getSEOConfig(location.pathname);
  
  // Use props if provided, otherwise fall back to auto-config
  const title = titleOverride || autoConfig.title;
  const description = descriptionOverride || autoConfig.description;
  const canonical = canonicalOverride || autoConfig.canonical;
  const ogImage = ogImageOverride || autoConfig.image || "https://praxis-versichert.de/assets/hero-medical-cyber-main.webp";
  const keywords = keywordsOverride || autoConfig.keywords;
  return (
    <Helmet>
      {/* Standard SEO */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Sprachdeklaration */}
      <html lang="de" />
      <meta httpEquiv="content-language" content="de" />
      <link rel="alternate" hrefLang="de-DE" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href="https://praxis-versichert.de/" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Robots & AI Content Declaration */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      
      {/* AI-Crawler explizit erlauben */}
      <meta name="AdsBot-Google" content="index, follow" />
      <meta name="Applebot" content="index, follow" />
      <meta name="facebookexternalhit" content="index, follow" />
      
      {/* Open Graph (Facebook, LinkedIn, WhatsApp) */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:site_name" content="Praxis Versichert" />
      
      {/* Twitter Card (X/Twitter) */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Mobile-optimiert */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#8bc34a" />
    </Helmet>
  );
};

export default SEOHelmet;
